import React from 'react';
import './i18next';
import { Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  ContactPage,
  PersonDetailsPage,
  MagazinePage,
  Header,
  Footer,
  MediaPage,
  SimplePage,
  ArticlePage,
  VenuePage,
  HomePage,
  NotFoundPage,
  SpeakersPage,
  SchedulePage,
  ScrollToTop,
} from '@kainos-applied-innovation/convention-frontend-components/';
import curators from './assets/2025/static-data/curators.json';
import speakers from './assets/2025/static-data/speakers.json';
import otherPeople from './assets/2025/static-data/otherPeople.json';
import schedule from './assets/2025/static-data/schedule.json';
import articles from './assets/2025/static-data/articles.json';
import headerInfo from './assets/2025/static-data/header.json';
import footerInfo from './assets/2025/static-data/footer.json';
import contactDetails from './assets/2025/static-data/contact.json';
import magazines from './assets/2025/static-data/magazines.json';

const RouteWrapper = ({ element, comingSoon, t }) => {
  if (comingSoon || !element) {
    return <SimplePage page="ComingSoon" t={t} />;
  }
  return element;
};

RouteWrapper.propTypes = {
  element: PropTypes.element,
  comingSoon: PropTypes.bool,
  t: PropTypes.func,
};

function App() {
  const { t } = useTranslation();
  const newsletterApiUrl = process.env.REACT_APP_BELTECH_NEWSLETTER_API_URL;
  const apiUrl = process.env.REACT_APP_BELTECH_EMAIL_API_URL;
  const recaptchaKey = process.env.REACT_APP_BELTECH_RECAPTCHA_KEY;
  const soldOut = process.env.REACT_APP_SOLD_OUT === 'true';
  const ticketsSoon = process.env.REACT_APP_TICKETS_SOON === 'true';
  const callForSpeakersExpirationDate = process.env.REACT_APP_CALL_FOR_SPEAKERS_EXPIRATION;
  const earlyBird = process.env.REACT_APP_EARLY_BIRD === 'true';
  const earlyBirdLink = process.env.REACT_APP_EARLY_BIRD_LINK || '';

  const routesConfig = [
    { path: '/*', element: <NotFoundPage t={t} /> },
    {
      path: '/',
      element: (
        <HomePage
          t={t}
          soldOut={soldOut}
          ticketsSoon={ticketsSoon}
          curatorsData={curators}
          callForSpeakersExpirationDate={callForSpeakersExpirationDate}
          earlyBird={earlyBird}
          earlyBirdLink={earlyBirdLink}
        />
      ),
    },
    { path: '/venue', element: <VenuePage t={t} /> },
    {
      path: '/speakers',
      element: <SpeakersPage t={t} speakersData={speakers} />,
    },
    {
      path: '/speakers/:speaker/:speakerId',
      element: (
        <PersonDetailsPage
          t={t}
          scheduleData={schedule}
          speakersList={speakers}
          page="speakers"
        />
      ),
    },
    {
      path: '/curators/:curator/:speakerId',
      element: (
        <PersonDetailsPage
          t={t}
          scheduleData={schedule}
          speakersList={curators}
          page="curators"
        />
      ),
    },
    {
      path: '/schedule',
      element: (
        <SchedulePage
          t={t}
          scheduleData={schedule}
          curatorsData={curators}
          speakersData={speakers}
          otherPeopleData={otherPeople}
        />
      ),
    },
    {
      path: '/contact',
      element: (
        <ContactPage
          t={t}
          contactDetails={contactDetails}
          apiUrl={apiUrl}
          recaptchaKey={recaptchaKey}
        />
      ),
    },
    { path: '/hospitality', element: <SimplePage t={t} page="Hospitality" /> },
    {
      path: '/magazines/:magazineId',
      element: <MagazinePage t={t} magazines={magazines} />,
    },
    { path: '/coming-soon', comingSoon: true },
    {
      path: '/media',
      element: <MediaPage mediaData={{ articles, magazines }} t={t} mediaDescriptions={{
        articles: 'Check out our articles featuring the latest news from Beltech.',
        magazines: 'Our magazines feature themes, and speakers from our events, as well as the advancements in the tech industry and opportunities for talent development.'
      }} />,
    },
    {
      path: '/articles/:articleId',
      element: <ArticlePage t={t} articles={articles} />,
    },
  ];

  return (
    <div className="con-app con-text zoomout-limited">
      <Header headerInfo={headerInfo} t={t} soldOut={soldOut} ticketsSoon={ticketsSoon} />
      <ScrollToTop />
      <div className="con-app__container ">
        <Routes>
          {routesConfig.map(({ path, element, comingSoon }) => (
            <Route
              key={path}
              path={path}
              element={
                <RouteWrapper element={element} comingSoon={comingSoon} t={t} />
              }
            />
          ))}
        </Routes>
      </div>
      <Footer footerInfo={footerInfo} apiUrl={newsletterApiUrl} />
    </div>
  );
}

export default App;